import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  input,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { UploadFilesComponent } from '@app/components/common/upload-files/upload-files.component';
import { ProjectAdminResponse } from '@app/models/admin.model';
import { FileUploadControl } from '@app/models/form.models';
import { FileType } from '@app/models/shared.enums';
import { NewProjectFormGroup } from '@app/pages/new-project-idea/new-project-form/new-project-form.helper';
import { CardComponent } from '@app/ui-components/card/card.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { ValidatedTextAreaComponent } from '@app/ui-components/validated-textarea/validated-textarea.component';
import { TranslateModule } from '@ngx-translate/core';
import { getNewFileFormGroup } from 'src/app/+helpers/form.helper';
import { GetLocalizedPipe } from 'src/app/+pipes/get-localized.pipe';
import { GenericStore } from 'src/app/+store/generic.store';

@Component({
  selector: 'app-project-idea-fields',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GetLocalizedPipe,
    CardComponent,
    ValidatedInputComponent,
    UploadFilesComponent,
    ValidatedTextAreaComponent,
    ValidatedDropdownComponent,
  ],
  templateUrl: './project-idea-fields.component.html',
  styleUrl: './project-idea-fields.component.scss',
})
export class ProjectIdeaFieldsComponent implements OnChanges {
  @Input({ required: true }) form!: FormGroup<NewProjectFormGroup>;
  @Input() project: ProjectAdminResponse | null = null;

  protected genericStore = inject(GenericStore);
  private fb = inject(FormBuilder);

  loading = input(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      const project = this.project;
      this.form.patchValue(
        {
          country: project.country ?? '',
          city: project.city ?? '',
          collectionCurrency: project.currency ?? '',
          projectTitle: project.name ?? '',
          projectIntroduction: project.description ?? '',
        },
        { emitEvent: false }
      );
      const filesFa = this.form.controls.files as FormArray<
        FormGroup<FileUploadControl>
      >;
      while (filesFa.length) filesFa.removeAt(0);

      const techSpecs = Array.isArray(project.technicalSpecifications)
        ? project.technicalSpecifications
        : [];
      if (!techSpecs.length) {
        filesFa.push(getNewFileFormGroup(this.fb, FileType.document));
      } else {
        for (const file of techSpecs) {
          const group = getNewFileFormGroup(this.fb, FileType.document);
          group.patchValue(
            {
              title: file?.name ?? '',
              description: file?.description ?? '',
              file: null,
            },
            { emitEvent: false }
          );
          filesFa.push(group);
        }
      }
    }
  }

  protected createNewFileGroup(
    formArray: FormArray<FormGroup<FileUploadControl>>
  ) {
    formArray.push(getNewFileFormGroup(this.fb, FileType.document));
    this.form.controls.files = formArray;
  }

  protected removeFileGroup($event: number) {
    this.form.controls.files.removeAt($event);
  }
}
